import { useAuth } from '@Context/CognitoAuthProvider';
import { ArtilleryAPI } from '@Lib/axios/artillery';
import { ArtilleryReport, AnalyzerWeeklyPrompt } from '@Models/types';
import { useEffect, useState } from 'react';
import ArtillerySidebar from '@Components/artillery-reports/ArtillerySidebar';
import ArtilleryMainPanel from '@Components/artillery-reports/ArtilleryMainPanel';

const ArtilleryReportsPage = () => {
  const { logout } = useAuth();
  const [reports, setReports] = useState<ArtilleryReport[] | null>(null);
  const [singleReport, setSingleReport] = useState<ArtilleryReport | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('Executive Summary');
  const [prompts, setPrompts] = useState<AnalyzerWeeklyPrompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  const fetchReports = async () => {
    setIsRefreshing(true);
    try {
      const artillery = ArtilleryAPI(logout);
      const reports = await artillery.getReports();
      setReports(reports);
      if (reports.length > 0) {
        setSingleReport(reports[0]);
      } else {
        setSingleReport(null);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const artillery = ArtilleryAPI(logout);
      const data = await artillery.getPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Error fetching artillery prompts:', error);
    } finally {
      setPromptsLoading(false);
    }
  };

  const createPrompt = async (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => {
    const artillery = ArtilleryAPI(logout);
    await artillery.createPrompt(prompt);
    await fetchPrompts();
  };

  const setDefaultPrompt = async (promptTitle: string, authorEmail: string) => {
    const artillery = ArtilleryAPI(logout);
    await artillery.setDefaultPrompt(promptTitle, authorEmail);
    await fetchPrompts();
  };

  const downloadPDF = async (fileName: string) => {
    setIsDownloading(true);
    try {
      const artillery = ArtilleryAPI(logout);
      await artillery.downloadPDF(fileName);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });

    // Add background transition animation
    if (element) {
      element.classList.add(
        'bg-slate-300',
        'transition-colors',
        'duration-500'
      );
      setTimeout(() => {
        element.classList.remove('bg-slate-300');
        element.classList.add('bg-white');
        setTimeout(() => {
          element.classList.remove(
            'bg-white',
            'transition-colors',
            'duration-500'
          );
        }, 500);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchPrompts();
  }, [logout]);

  useEffect(() => {
    console.log('Single Report:', singleReport);
  }, [singleReport]);

  return (
    <div className="flex h-screen w-full flex-row gap-2 overflow-hidden p-6">
      <ArtillerySidebar
        reports={reports}
        singleReport={singleReport}
        setSingleReport={setSingleReport}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        fetchReports={fetchReports}
        isRefreshing={isRefreshing}
      />
      <ArtilleryMainPanel
        singleReport={singleReport}
        isDownloading={isDownloading}
        downloadPDF={downloadPDF}
        isNavDropdownOpen={isNavDropdownOpen}
        setIsNavDropdownOpen={setIsNavDropdownOpen}
        currentSection={currentSection}
        setCurrentSection={setCurrentSection}
        scrollToSection={scrollToSection}
        prompts={prompts}
        promptsLoading={promptsLoading}
        onCreatePrompt={createPrompt}
        onSetDefaultPrompt={setDefaultPrompt}
        onViewPrompt={() => {
          /* No-op placeholder: could open modal if needed */
        }}
      />
    </div>
  );
};

export default ArtilleryReportsPage;
